import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const Banner = () => {
  const navigate = useNavigate();
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;
  const [banners, setBanners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({ id: '', category: 'home', url: '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await apiService.get('bannerList');
      if (response.data) {
        setBanners(response.data);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  };

  const openAddModal = () => {
    setFormData({ id: '', category: 'home', url: '' });
    setImageFile(null);
    setImagePreview('');
    setShowModal(true);
  };

  const editBanner = (banner) => {
    setFormData({ id: banner._id, category: banner.category || 'home', url: banner.url || '' });
    setImageFile(null);
    if (banner.image) {
      setImagePreview(`${mediaUrl}${banner.image}`);
    } else {
      setImagePreview('');
    }
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        e.target.value = '';
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.id && !imageFile) {
      toast.error('Please select file');
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const submitData = new FormData();
      if (formData.id) submitData.append('id', formData.id);
      submitData.append('category', formData.category);
      submitData.append('url', formData.url);
      if (imageFile) submitData.append('image', imageFile);

      const response = await apiService.postFormData('updateBanner', submitData);

      if (response.success) {
        toast.error(response.message);
      } else {
        toast.success('Banner updated successfully');
        setShowModal(false);
        fetchBanners();
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (id) => {
    try {
      await apiService.get(`bannerStatus/${id}`);
      fetchBanners();
    } catch (error) {
      toast.error('Something went wrong!');
      console.error(error);
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const deleteBanner = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const response = await apiService.get(`deleteBanner/${deleteId}`);
      if (response.success) {
        toast.error(response.message);
      } else {
        toast.success('Banner deleted successfully');
        setShowDeleteModal(false);
        fetchBanners();
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      home: 'bg-[#009ef7]/10 text-[#009ef7]',
      coupon: 'bg-green-100 text-green-800',
      influencer: 'bg-cyan-100 text-cyan-800',
      offer: 'bg-purple-100 text-purple-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="bg-white rounded-lg shadow-sm m-0">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Banners</h1>
            <nav className="flex text-sm text-gray-600">
              <span className="cursor-pointer hover:text-[#009ef7]" onClick={() => navigate('/dashboard')}>Home</span>
              <span className="mx-2">/</span>
              <span className="text-gray-900">Banners</span>
            </nav>
          </div>
          <button
            onClick={openAddModal}
            className="px-2 sm:px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#009ef7' }}
          >
            <i className="bi bi-plus text-sm mr-1"></i>
            Add New
          </button>
        </div>

        <div className="p-2">
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm">
                  <th className="px-4 py-3 text-left font-semibold min-w-[225px]">Banner</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[125px]">Category</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[125px]">Url</th>
                  <th className="px-4 py-3 text-left font-semibold min-w-[125px]">Status</th>
                  <th className="px-4 py-3 text-center font-semibold min-w-[125px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {banners.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      No banners found
                    </td>
                  </tr>
                ) : (
                  banners.map((banner) => (
                    <tr key={banner._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <img
                          src={`${mediaUrl}${banner.image}`}
                          alt="Banner"
                          className="w-48 h-24 object-cover rounded"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="95" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E';
                          }}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${getCategoryBadge(banner.category)}`}>
                          {banner.category ? banner.category.charAt(0).toUpperCase() + banner.category.slice(1) : 'Home'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-900">{banner.url || '--'}</td>
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={banner.status == 1}
                            onChange={() => toggleStatus(banner._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => editBanner(banner)}
                            className="px-3 py-1 text-xs font-semibold rounded"
                            style={{ backgroundColor: '#d4f4dd', color: '#1bc943' }}
                          >
                            <i className="bi bi-pencil text-sm"></i>
                          </button>
                          <button
                            onClick={() => confirmDelete(banner._id)}
                            className="px-3 py-1 text-xs font-semibold rounded"
                            style={{ backgroundColor: '#ffe2e5', color: '#f1416c' }}
                          >
                            <i className="bi bi-trash text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b shrink-0">
              <h3 className="text-lg font-semibold">Update Banner</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 flex flex-col justify-between">
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                    >
                      <option value="home">Home</option>
                      <option value="coupon">Coupon</option>
                      <option value="influencer">Influencer</option>
                      <option value="offer">Offer</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Url <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="Enter banner url"
                      required
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <div
                        className="w-72 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden"
                        style={{
                          backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        {!imagePreview && <i className="bi bi-image text-4xl text-gray-400"></i>}
                      </div>
                      <label
                        className="absolute bottom-2 right-2 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center cursor-pointer hover:bg-gray-50"
                        title="Change banner"
                      >
                        <i className="bi bi-pencil text-sm" style={{ color: '#009ef7' }}></i>
                        <input
                          type="file"
                          accept=".png, .jpg, .jpeg, .webp"
                          onChange={handleImageChange}
                          required={!formData.id && !imagePreview}
                          className="hidden"
                        />
                      </label>
                      {imagePreview && (
                        <button
                          type="button"
                          onClick={() => { setImagePreview(''); setImageFile(null); }}
                          className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center hover:bg-gray-50"
                          title="Remove banner"
                        >
                          <i className="bi bi-x text-lg text-gray-600"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Allowed file types: png, jpg, jpeg, webp (Max 5MB)</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed disabled:pointer-events-none flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  {saving ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Submit</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => !deleting && setShowDeleteModal(false)} disabled={deleting} className="text-gray-400 hover:text-gray-600 disabled:pointer-events-none">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Are you sure you want to delete this Banner?</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              >
                Cancel
              </button>
              <button
                onClick={deleteBanner}
                disabled={deleting}
                className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed disabled:pointer-events-none flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banner;
